var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useMemo, Children, cloneElement, isValidElement, useEffect, useRef, useCallback, } from "react";
import { trapEvent } from "../../utils/EventHelpers";
import { getByCircularIndex, isValidIndex } from "../../utils/ArrayHelpers";
import { getItemSize, StyledMenu } from "./Menu.style";
import MenuContext from "./MenuContext";
import MenuItem from "./MenuItem";
import useUniqueId from "../../hooks/useUniqueId";
import MenuGroup from "./MenuGroup";
import { StyledExpandableContent, StyledMenuItem } from "./MenuItem.style";
import { useVirtualizer } from "@tanstack/react-virtual";
import Box from "../box/Box";
import { focusVisibleAttribute } from "../../utils/focusVisible";
const prefixId = "nx-menu-";
// how many items to load outside the viewing window
export const virtualizerOverscanItems = 25;
export const focusVisibleClassName = "focus-visible";
const EXPANDABLE_ITEM_SELECTOR = '[aria-expanded="true"]';
function searchMenuItemSelection(item, multiSelect, preSelected) {
    if (item.props.selected) {
        if (multiSelect) {
            preSelected.push(item.props.id);
        }
        else {
            return item.props.id;
        }
    }
    return "";
}
function searchIndexesAndSelection(childrenList, multiSelect, preSelected, menuIndexes) {
    Children.forEach(childrenList, (child) => {
        if (isValidElement(child)) {
            if (child.type === MenuGroup) {
                const group = child;
                if (group.props.children) {
                    preSelected =
                        searchIndexesAndSelection(group.props.children, multiSelect, preSelected, menuIndexes) || preSelected;
                }
            }
            else {
                if (child.type === MenuItem) {
                    preSelected = searchMenuItemSelection(child, multiSelect, preSelected) || preSelected;
                }
                if (!child.props.disabled) {
                    menuIndexes.push(child.props.id);
                }
            }
        }
        else {
            menuIndexes.push("");
        }
    });
    return multiSelect ? "" : preSelected;
}
const getMenuIndexes = (children, multiSelect) => () => {
    let preSelected = multiSelect ? [] : "";
    const menuIndexes = [];
    preSelected = searchIndexesAndSelection(children, multiSelect, preSelected, menuIndexes) || preSelected;
    return {
        menuIndexes,
        preSelected,
    };
};
const getMenuElementNumbers = (childrenList) => {
    let nb = 0;
    Children.forEach(childrenList, (child) => {
        if (isValidElement(child)) {
            if (child.type === MenuGroup) {
                nb++;
                const group = child;
                if (group.props.children) {
                    nb = nb + getMenuElementNumbers(group.props.children);
                }
            }
            else if (child.type === MenuItem) {
                nb++;
            }
        }
    });
    return nb;
};
const getInitialFocusItem = (preSelected, menuIndexes, multiSelect) => {
    if (preSelected && !multiSelect && menuIndexes.includes(preSelected)) {
        return preSelected;
    }
    else if (multiSelect && preSelected.length > 0) {
        const preselectedToFocus = preSelected.find((el) => menuIndexes.includes(el));
        if (preselectedToFocus) {
            return preselectedToFocus;
        }
    }
    if (menuIndexes && menuIndexes[0]) {
        return menuIndexes[0];
    }
    return "";
};
export const useHandleCircularUpDown = (menuIndexes, focusItem, setFocusItem, allowTab, trapTab = false) => {
    return (e) => {
        var _a, _b, _c, _d;
        const keyCode = e.key;
        if (keyCode === "Tab") {
            if (!allowTab) {
                trapTab && trapEvent(e);
            }
            else {
                (_b = (_a = document.getElementById(focusItem)) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, { block: "nearest" });
            }
        }
        if (keyCode === "ArrowUp" || keyCode === "ArrowDown") {
            // move the focus to the previous or next item
            trapEvent(e);
            const currentIndex = menuIndexes.indexOf(focusItem);
            const newCircularIndex = keyCode === "ArrowUp" ? (currentIndex >= 0 ? currentIndex - 1 : -1) : currentIndex >= 0 ? currentIndex + 1 : 0;
            const newIndex = getByCircularIndex(menuIndexes, newCircularIndex);
            setFocusItem(newIndex);
            // TODO: this is not working correctly when the item is wrapped on a WithTooltip
            (_d = (_c = document.getElementById(newIndex)) === null || _c === void 0 ? void 0 : _c.scrollIntoView) === null || _d === void 0 ? void 0 : _d.call(_c, { block: "nearest" });
        }
    };
};
export const useFixFocusVisible = (id) => {
    // on component did mount we remove the visibility of the focus on the element
    // but it would appear as soon as we press any key
    // we have to do that because the .focus() display the focus
    useEffect(() => {
        var _a;
        (_a = document.querySelector(`[id="${id}"] [${focusVisibleAttribute}]`)) === null || _a === void 0 ? void 0 : _a.removeAttribute(focusVisibleAttribute);
    }, []);
};
const Menu = (_a) => {
    var { id, children, isSelectable = true, size = "medium", variant = "classic", "aria-label": ariaLabel, onSelect, menuVisibleItems, multiSelect = false, onMultiSelect, drawFocusRingWithoutFocusing = false, focusedIndex = drawFocusRingWithoutFocusing ? undefined : 0, virtualized = false, allowTab = false, trapTab = true, onKeyDown, isContentWidth = false } = _a, rest = __rest(_a, ["id", "children", "isSelectable", "size", "variant", "aria-label", "onSelect", "menuVisibleItems", "multiSelect", "onMultiSelect", "drawFocusRingWithoutFocusing", "focusedIndex", "virtualized", "allowTab", "trapTab", "onKeyDown", "isContentWidth"]);
    id = useUniqueId(prefixId, id);
    // get the indexes of the menu items used to navigate with ArrowUp/Down
    // and the pre-selected item if any
    const { menuIndexes, preSelected } = useMemo(getMenuIndexes(children, multiSelect), [children]);
    const menuElementsNb = useMemo(() => {
        return getMenuElementNumbers(children);
    }, [children]);
    const [selectedItem, setSelectedItem] = useState(preSelected);
    const [hasExpandedContent, setHasExpandedContent] = useState(false);
    const [focusItem, setFocusItem] = useState(() => {
        return focusedIndex !== undefined && isValidIndex(menuIndexes, focusedIndex) && !drawFocusRingWithoutFocusing
            ? getInitialFocusItem(preSelected, menuIndexes, multiSelect)
            : "";
    });
    const handleUpAndDown = useHandleCircularUpDown(menuIndexes, focusItem, setFocusItem, allowTab, trapTab);
    const handleOnKeyDown = (e) => {
        handleUpAndDown(e);
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    };
    const parentRef = useRef(null);
    const memoChildren = useMemo(() => Children.toArray(children).filter(isValidElement), [children]);
    const visibleItems = useMemo(() => menuVisibleItems && Math.min(menuVisibleItems, menuElementsNb), [menuVisibleItems, menuElementsNb]);
    const estimateSize = useCallback((index) => {
        var _a, _b;
        const menuChild = memoChildren[index];
        const itemSize = getItemSize({ size, multiSelect });
        if (menuChild.type === MenuGroup) {
            return (1 + Children.count(menuChild.props.children)) * itemSize;
        }
        // expanded menu item
        if (menuChild.props.expandableContent && menuChild.props.id === selectedItem) {
            const el = (_a = parentRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(`${EXPANDABLE_ITEM_SELECTOR} ${StyledExpandableContent}`);
            return itemSize + (((_b = el === null || el === void 0 ? void 0 : el[0]) === null || _b === void 0 ? void 0 : _b.clientHeight) || 0);
        }
        return itemSize;
    }, [memoChildren, size, multiSelect, selectedItem, hasExpandedContent]);
    const menuFullHeight = useMemo(() => {
        if (visibleItems) {
            return visibleItems * getItemSize({ size, multiSelect });
        }
        // in case it's a group, its children will be counted too
        return memoChildren.reduce((sum, _, index) => sum + estimateSize(index), 0);
    }, [visibleItems, memoChildren, size, multiSelect, hasExpandedContent]);
    const rowVirtualizer = useVirtualizer({
        count: memoChildren.length,
        overscan: virtualizerOverscanItems,
        getScrollElement: () => parentRef.current,
        estimateSize,
    });
    useEffect(() => {
        setSelectedItem(preSelected);
    }, [preSelected]);
    // set focus ring on first item without focusing it
    useEffect(() => {
        const validIndex = focusedIndex !== undefined && isValidIndex(menuIndexes, focusedIndex);
        if (!drawFocusRingWithoutFocusing || !validIndex) {
            return;
        }
        // remove ring from previously focused item
        const currentFocused = document.querySelector(`[id="${id}"] [${focusVisibleAttribute}]`);
        currentFocused === null || currentFocused === void 0 ? void 0 : currentFocused.removeAttribute(focusVisibleAttribute);
        const index = focusedIndex !== undefined && validIndex ? focusedIndex : 0;
        const firstItem = document.querySelector(`[id="${id}"] ${StyledMenuItem}[data-index="${index}"]`);
        if (!firstItem) {
            return;
        }
        // TODO: focus-visible class will disappear from the menu soon
        //       to either use hover or focus style :/
        firstItem.classList.add(focusVisibleClassName);
        firstItem.setAttribute(focusVisibleAttribute, "");
        if (virtualized) {
            rowVirtualizer.scrollToIndex(index);
        }
        else {
            firstItem.scrollIntoView({ block: "nearest" });
        }
    }, [children, focusedIndex]);
    const handleSelectedItem = useCallback((menuItemId, isItemExpanded, event) => {
        if (isSelectable) {
            if (multiSelect) {
                const setObj = new Set(selectedItem);
                const newItems = setObj.has(menuItemId)
                    ? selectedItem.filter((i) => i !== menuItemId)
                    : [...selectedItem, menuItemId];
                setSelectedItem(newItems);
                event && (onMultiSelect === null || onMultiSelect === void 0 ? void 0 : onMultiSelect(newItems, menuItemId));
                return;
            }
            else {
                setSelectedItem(menuItemId);
            }
            setFocusItem(menuItemId);
        }
        // call the callback only when it's an action from the user
        // avoid to call it when the item is pre-selected programatically (no event)
        onSelect && event && onSelect(menuItemId, isItemExpanded, event);
        setHasExpandedContent(isItemExpanded);
        virtualized && rowVirtualizer.measure();
    }, [
        multiSelect,
        onSelect,
        virtualized,
        isSelectable,
        selectedItem,
        onMultiSelect,
        setSelectedItem,
        setFocusItem,
        setHasExpandedContent,
    ]);
    const contextValue = useMemo(() => ({
        selectedItem,
        focusItem,
        setSelectedItem: handleSelectedItem,
        setFocusItem,
        multiSelect,
        isContentWidth,
    }), [selectedItem, focusItem, onSelect, multiSelect, isContentWidth]);
    const cloneMenuChild = useCallback((child, index, props) => cloneElement(child, Object.assign({ size,
        variant, key: index, "data-index": index }, props)), [size, variant]);
    return (React.createElement(StyledMenu, Object.assign({ id: id, "data-virtualized": virtualized, variant: variant, menuVisibleItems: visibleItems, size: size, multiSelect: multiSelect }, (variant === "select" && multiSelect && { "aria-multiselectable": true }), { "aria-label": ariaLabel }, (variant !== "timeframepicker" && {
        role: variant === "select" ? "listbox" : "menu",
    }), { "data-a11y-ignore": "aria-required-children", onKeyDown: handleOnKeyDown, ref: parentRef, style: virtualized ? { height: menuFullHeight } : {} }, rest),
        React.createElement(MenuContext.Provider, { value: contextValue }, virtualized ? (React.createElement(Box, { height: `${rowVirtualizer.getTotalSize()}px` }, rowVirtualizer.getVirtualItems().map((virtualRow) => cloneMenuChild(memoChildren[virtualRow.index], virtualRow.index, {
            style: {
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
                boxSizing: "border-box",
            },
        })))) : (memoChildren.map((child, i) => cloneMenuChild(child, i))))));
};
export default Menu;
//# sourceMappingURL=Menu.js.map